"use client";
import { Controller, useFormContext } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Facility } from "@/types/facility";
import { BookingType, ComparisonCondition, Gender } from "@/types/enums";
import { useGetMembershipListQuery } from "@/services/membershipsApi";
import { useGetMemberGroupsQuery } from "@/services/memberGroupApis";
import { useMemo } from "react";
import Combobox from "@/components/ui/common/combobox";

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

export default function EligibilityForm({
  action,
  open,
}: FormStepProps) {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<Facility>();

  const { data: membershipData } = useGetMembershipListQuery();
  const { data: memberGroupData, refetch: refetchGroups } =
    useGetMemberGroupsQuery();

  const memberGroupOptions = useMemo(() => {
    return Array.isArray(memberGroupData?.data)
      ? memberGroupData?.data.map((group) => {
          return {
            value: group.id?.toString() || "",
            label: group.name || "",
          };
        })
      : [];
  }, [memberGroupData]);

  const membershipOptions = useMemo(() => {
    return Array.isArray(membershipData)
      ? membershipData.map((membership) => {
          return {
            value: membership.id?.toString() || "",
            label: membership.name || "",
          };
        })
      : [];
  }, [membershipData]);
  // Watch toggle values for conditional rendering
  const genderRestricted = watch("eligibility.gender_restricted");
  const membershipRestricted = watch("eligibility.membership_plan_restricted");
  const ageRestricted = watch("eligibility.age_restricted");
  const pointsRestricted = watch("eligibility.reward_points_restricted");
  const groupRestricted = watch("eligibility.customer_group_restricted");

  return (
    <Card className="p-4 space-y-4">
      <p className="font-medium text-base">Eligibility</p>
      <Separator className="h-[1px]" />

      <div className="flex items-center gap-2">
        {/* Gender Section */}
        <div className="gap-2 flex items-center">
          <div className="flex items-center gap-2">
            <Controller
              name="eligibility.gender_restricted"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label>Gender</Label>
          </div>

          {genderRestricted && (
            <div className="">
              <Controller
                name="eligibility.allowed_genders"
                control={control}
                rules={{ required: genderRestricted ? "Required" : false }}
                render={({ field }) => (
                  <div className="flex rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => field.onChange([Gender.Male])}
                      className={cn(
                        "py-1 px-3 h-10 text-sm w-full",
                        field.value?.includes(Gender.Male)
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Male
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange([Gender.Female])}
                      className={cn(
                        "py-1 px-3 h-10 text-sm w-full",
                        field.value?.includes(Gender.Female)
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-gray-700"
                      )}
                    >
                      Female
                    </button>
                  </div>
                )}
              />
              {errors.eligibility?.allowed_genders && (
                <span className="text-red-500 text-xs">
                  {errors.eligibility.allowed_genders.message}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Membership Plan Section */}
        <div className="gap-2 flex items-center">
          <div className="flex items-center gap-2">
            <Controller
              name="eligibility.membership_plan_restricted"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label>Membership Plan</Label>
          </div>

          {membershipRestricted && (
            <div className="">
              <Controller
                name="eligibility.allowed_membership_plans"
                control={control}
                rules={{ required: membershipRestricted ? "Required" : false }}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={membershipOptions}
                    defaultValue={
                      membershipOptions.find(
                        (option) => option.value === value?.[0]?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = membershipOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? [Number(selectedOption.value)]
                          : undefined
                      );
                    }}
                    className="w-full line-clamp-1 max-w-48"
                    label="Membership Plan"
                    showLabel={false}
                  />
                )}
              />
              {errors.eligibility?.allowed_membership_plans && (
                <span className="text-red-500 text-xs">
                  {errors.eligibility.allowed_membership_plans.message}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Customer Group Section */}
        <div className="gap-2 flex items-center">
          <div className="flex items-center gap-2">
            <Controller
              name="eligibility.customer_group_restricted"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label>Customer Group</Label>
          </div>

          {groupRestricted && (
            <div className="">
              <Controller
                name="eligibility.allowed_customer_groups"
                control={control}
                rules={{ required: groupRestricted ? "Required" : false }}
                render={({ field: { value, onChange } }) => (
                  <Combobox
                    list={memberGroupOptions}
                    defaultValue={
                      memberGroupOptions.find(
                        (option) => option.value === value?.[0]?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = memberGroupOptions.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? [Number(selectedOption.value)]
                          : undefined
                      );
                    }}
                    className="w-full line-clamp-1 max-w-48"
                    label="Member Group"
                    showLabel={false}
                  />
                )}
              />
              {errors.eligibility?.allowed_customer_groups && (
                <span className="text-red-500 text-xs">
                  {errors.eligibility.allowed_customer_groups.message}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 items-center">
        {/* Booking Section */}
        <div className="gap-2 flex items-center">
          <Label>Booking Type</Label>

          <div className="">
            <Controller
              name="booking_type"
              control={control}
              render={({ field }) => (
                <div className="flex rounded-md overflow-hidden">
                  <button
                    type="button"
                    onClick={() => field.onChange(BookingType.Public)}
                    className={cn(
                      "py-1 px-2  h-10 text-xs w-full",
                      field.value == BookingType.Public
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-700"
                    )}
                  >
                    Public
                  </button>
                  <button
                    type="button"
                    onClick={() => field.onChange(BookingType.MemberOnly)}
                    className={cn(
                      "py-1 px-2  h-10 text-xs w-full",
                      field.value == BookingType.MemberOnly
                        ? "bg-primary text-white"
                        : "bg-gray-100 text-gray-700"
                    )}
                  >
                    <span className="text-nowrap">Member Only</span>
                  </button>
                </div>
              )}
            />
            {errors.booking_type && (
              <span className="text-red-500 text-xs">
                {errors.booking_type.message as string}
              </span>
            )}
          </div>
        </div>

        {/* Age Section */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Controller
              name="eligibility.age_restricted"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label>Age</Label>
          </div>

          {ageRestricted && (
            <div className="flex items-center gap-2">
              <Controller
                name="eligibility.age_condition"
                control={control}
                rules={{ required: ageRestricted ? "Required" : false }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select Condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ComparisonCondition.GreaterThan}>
                        Greater than
                      </SelectItem>
                      <SelectItem value={ComparisonCondition.LessThan}>
                        Less than
                      </SelectItem>
                      <SelectItem value={ComparisonCondition.EqualTo}>
                        Equal to
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <Controller
                name="eligibility.age_value"
                control={control}
                rules={{
                  required: ageRestricted ? "Required" : false,
                  min: { value: 0, message: "Age must be positive" },
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    placeholder="Enter age"
                    className="w-16"
                    {...field}
                  />
                )}
              />
              {errors.eligibility?.age_value && (
                <span className="text-red-500 text-xs">
                  {errors.eligibility.age_value.message}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Reward Points Section */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <Controller
              name="eligibility.reward_points_restricted"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Label>Reward Points</Label>
          </div>

          {pointsRestricted && (
            <div className="flex items-center gap-2">
              <Controller
                name="eligibility.reward_points_condition"
                control={control}
                rules={{ required: pointsRestricted ? "Required" : false }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select Condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ComparisonCondition.GreaterThan}>
                        Greater than
                      </SelectItem>
                      <SelectItem value={ComparisonCondition.LessThan}>
                        Less than
                      </SelectItem>
                      <SelectItem value={ComparisonCondition.EqualTo}>
                        Equal to
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              <Controller
                name="eligibility.reward_points_value"
                control={control}
                rules={{
                  required: pointsRestricted ? "Required" : false,
                  min: { value: 0, message: "Points must be positive" },
                }}
                render={({ field }) => (
                  <Input
                    type="number"
                    placeholder="Enter points"
                    className="w-16"
                    {...field}
                  />
                )}
              />
              {errors.eligibility?.reward_points_value && (
                <span className="text-red-500 text-xs">
                  {errors.eligibility.reward_points_value.message}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
